import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
    CircularProgress,
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    Close as CloseIcon,
    History as HistoryIcon,
} from "@mui/icons-material";
import {
    getVpnUsers,
    createVpnUser,
    updateVpnUser,
    deleteVpnUser,
    getVpnUserAccessLogs,
    getVpnSessions,
} from "../api/vpnApi";
import { getTenants } from "../api/tenantApi";

export default function VpnUsers() {
    const accessToken = useSelector((s) => s.session?.accessToken);
    const me = useSelector((s) => s.session?.me);
    const isSuperAdmin = me?.global_role === "superadmin"; 

    const [vpnUsers, setVpnUsers] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [vpnUsersTotal, setVpnUsersTotal] = useState(0);
    const [vpnUsersOffset, setVpnUsersOffset] = useState(0);
    const [vpnUsersLimit] = useState(20);
    const [loadingMore, setLoadingMore] = useState(false);
    const [vpnUsersSearch, setVpnUsersSearch] = useState("");

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createForm, setCreateForm] = useState({
        username: "",
        email: "",
        full_name: "",
        allowed_tenants: [],
        password: "",
    });
    const [creating, setCreating] = useState(false);

    const [showEditModal, setShowEditModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [editForm, setEditForm] = useState({
        email: "",
        full_name: "",
        allowed_tenants: [],
        is_active: true,
        password: "",
    });
    const [updating, setUpdating] = useState(false);

    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deletingUser, setDeletingUser] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [showLogsDialog, setShowLogsDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [accessLogs, setAccessLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [accessLogsSearch, setAccessLogsSearch] = useState("");

    const [showSessionsDialog, setShowSessionsDialog] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [sessionsTotal, setSessionsTotal] = useState(0);
    const [sessionsOffset, setSessionsOffset] = useState(0);
    const [sessionsLimit] = useState(50);
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [loadingMoreSessions, setLoadingMoreSessions] = useState(false);
    const [sessionsSearch, setSessionsSearch] = useState("");

    useEffect(() => {
        if (accessToken) {
            fetchVpnUsers();
            fetchTenants();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken]);

    useEffect(() => {
        if (!accessToken) return;
        const t = setTimeout(() => {
            fetchVpnUsers(false);
        }, 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vpnUsersSearch]);

    const fetchVpnUsers = async (loadMore = false) => {
        if (loadMore) {
            setLoadingMore(true);
        } else {
            setLoading(true);
            setVpnUsersOffset(0);
        }

        try {
            const currentOffset = loadMore ? vpnUsersOffset : 0;
            const data = await getVpnUsers(accessToken, {
                limit: vpnUsersLimit,
                offset: currentOffset,
                search: vpnUsersSearch.trim() || undefined,
            });

            const items = data.items || [];
            const total = data.total || 0;

            if (loadMore) {
                setVpnUsers(prev => [...prev, ...items]);
            } else {
                setVpnUsers(items);
            }

            setVpnUsersTotal(total);
            setVpnUsersOffset(currentOffset + items.length);
        } catch (err) {
            console.error("VPN kullanıcıları alınamadı:", err);
            toast.error("VPN kullanıcıları alınamadı!");
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const fetchTenants = async () => {
        try {
            const data = await getTenants(accessToken);
            setTenants(data.items || data || []);
        } catch (err) {
            console.error("Tenant listesi alınamadı:", err);
        }
    };

    // ============== CREATE VPN USER ==============

    const handleCreateModalOpen = () => {
        setCreateForm({
            username: "",
            email: "",
            full_name: "",
            allowed_tenants: [],
            password: "",
        });
        setShowCreateModal(true);
    };

    const handleCreateSubmit = async () => {
        if (!createForm.username.trim()) {
            toast.error("Username zorunlu!");
            return;
        }
        if (!createForm.email.trim()) {
            toast.error("Email zorunlu!");
            return;
        }
        if (createForm.allowed_tenants.length === 0) {
            toast.error("En az bir tenant seçmelisiniz!");
            return;
        }
        if (!createForm.password.trim()) {
            toast.error("Şifre zorunlu!");
            return;
        }

        setCreating(true);
        try {
            await createVpnUser(accessToken, createForm);
            toast.success("VPN kullanıcısı oluşturuldu!");
            setShowCreateModal(false);
            fetchVpnUsers();
        } catch (err) {
            console.error("VPN kullanıcısı oluşturulamadı:", err);
            toast.error(err.response?.data?.detail || "VPN kullanıcısı oluşturulamadı!");
        } finally {
            setCreating(false);
        }
    };

    // ============== EDIT VPN USER ==============

    const handleEditModalOpen = (user) => {
        setEditingUser(user);
        setEditForm({
            email: user.email,
            full_name: user.full_name || "",
            allowed_tenants: user.allowed_tenants,
            is_active: user.is_active,
            password: "",
        });
        setShowEditModal(true);
    };

    const handleEditSubmit = async () => {
        setUpdating(true);
        try {
            await updateVpnUser(accessToken, editingUser.id, editForm);
            toast.success("VPN kullanıcısı güncellendi!");
            setShowEditModal(false);
            fetchVpnUsers();
        } catch (err) {
            console.error("VPN kullanıcısı güncellenemedi:", err);
            toast.error(err.response?.data?.detail || "VPN kullanıcısı güncellenemedi!");
        } finally {
            setUpdating(false);
        }
    };

    // ============== DELETE VPN USER ==============

    const handleDeleteDialogOpen = (user) => {
        setDeletingUser(user);
        setShowDeleteDialog(true);
    };

    const handleDeleteConfirm = async () => {
        setDeleting(true);
        try {
            const result = await deleteVpnUser(accessToken, deletingUser.id);
            const username = result.username || deletingUser.username;
            toast.success(`${username} silindi!`);
            setShowDeleteDialog(false);
            fetchVpnUsers();
        } catch (err) {
            console.error("VPN kullanıcısı silinemedi:", err);
            toast.error(err.response?.data?.detail || "VPN kullanıcısı silinemedi!");
        } finally {
            setDeleting(false);
        }
    };

    // ============== ACCESS LOGS ==============

    const fetchAccessLogs = async (user, searchTerm = "") => {
        setLoadingLogs(true);
        try {
            const logs = await getVpnUserAccessLogs(accessToken, user.id, {
                search: searchTerm.trim() || undefined,
            });
            setAccessLogs(logs);
        } catch (err) {
            console.error("Access logları alınamadı:", err);
            toast.error("Access logları alınamadı!");
        } finally {
            setLoadingLogs(false);
        }
    };

    const handleLogsDialogOpen = async (user) => {
        setSelectedUser(user);
        setAccessLogsSearch("");
        setShowLogsDialog(true);
        await fetchAccessLogs(user, "");
    };

    // ============== VPN SESSION LOGS ==============

    const fetchSessions = async (loadMore = false) => {
        if (loadMore) {
            setLoadingMoreSessions(true);
        } else {
            setLoadingSessions(true);
            setSessionsOffset(0);
        }

        try {
            const currentOffset = loadMore ? sessionsOffset : 0;
            const data = await getVpnSessions(accessToken, {
                limit: sessionsLimit,
                offset: currentOffset,
                search: sessionsSearch.trim() || undefined,
            });
            const items = data.sessions || [];
            if (loadMore) {
                setSessions(prev => [...prev, ...items]);
            } else {
                setSessions(items);
            }
            setSessionsTotal(data.total || 0);
            setSessionsOffset(currentOffset + items.length);
        } catch (err) {
            console.error("Session logları alınamadı:", err);
            toast.error("Session logları alınamadı!");
        } finally {
            setLoadingSessions(false);
            setLoadingMoreSessions(false);
        }
    };

    const handleSessionsDialogOpen = () => {
        setSessionsSearch("");
        setShowSessionsDialog(true);
        fetchSessions();
    };

    useEffect(() => {
        if (!showSessionsDialog || !accessToken) return;
        const t = setTimeout(() => {
            fetchSessions(false);
        }, 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionsSearch, showSessionsDialog, accessToken]);

    useEffect(() => {
        if (!showLogsDialog || !selectedUser || !accessToken) return;
        const t = setTimeout(() => {
            fetchAccessLogs(selectedUser, accessLogsSearch);
        }, 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessLogsSearch, showLogsDialog, selectedUser, accessToken]);

    // ============== TENANT SELECTION HELPERS ==============

    const handleTenantChange = (event, formType) => {
        const value = event.target.value;

        if (value.includes("*")) {
            if (formType === "create") {
                setCreateForm({ ...createForm, allowed_tenants: ["*"] });
            } else {
                setEditForm({ ...editForm, allowed_tenants: ["*"] });
            }
        } else {
            if (formType === "create") {
                setCreateForm({ ...createForm, allowed_tenants: value });
            } else {
                setEditForm({ ...editForm, allowed_tenants: value });
            }
        }
    };

    const getTenantNames = (allowedTenants) => {
        if (allowedTenants.includes("*")) {
            return "Tümü";
        }
        return allowedTenants
            .map((tid) => tenants.find((t) => t.id === tid)?.name || tid)
            .join(", ");
    };

    const formatDuration = (seconds) => {
        if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) {
            return "-";
        }
        const total = Math.max(0, Number(seconds));
        const h = Math.floor(total / 3600);
        const m = Math.floor((total % 3600) / 60);
        const s = total % 60;
        if (h > 0) {
            return `${h}s ${m}dk ${s}sn`;
        }
        if (m > 0) {
            return `${m}dk ${s}sn`;
        }
        return `${s}sn`;
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!isSuperAdmin) {
        return (
            <Box sx={{ textAlign: "center", mt: 8, color: "#e6edf3" }}>
                <Typography variant="h6" sx={{ color: "#f85149" }}>
                    Bu sayfaya erişim yetkiniz yok.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ color: "#e6edf3" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: "bold", color: "#e6edf3" }}>
                    🔐 VPN Kullanıcıları
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<HistoryIcon />}
                        onClick={handleSessionsDialogOpen}
                        sx={{ color: "#58a6ff", borderColor: "#58a6ff", "&:hover": { borderColor: "#79c0ff", color: "#79c0ff" } }}
                    >
                        Session Logları
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleCreateModalOpen}
                        sx={{ backgroundColor: "#238636", "&:hover": { backgroundColor: "#2ea043" } }}
                    >
                        Yeni VPN Kullanıcısı
                    </Button>
                </Box>
            </Box>

            <TableContainer
                component={Paper}
                sx={{
                    backgroundColor: "#161b22",
                    border: "1px solid #30363d",
                    borderRadius: "8px",
                }}
            >
                <Box sx={{ p: 2, borderBottom: "1px solid #30363d" }}>
                    <TextField
                        sx={{ backgroundColor: "#fff" }}
                        size="small"
                        fullWidth
                        label="VPN kullanıcısı ara (username, email, ad soyad)"
                        value={vpnUsersSearch}
                        onChange={(e) => setVpnUsersSearch(e.target.value)}
                    />
                </Box>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: "#21262d" }}>
                            <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Username</TableCell>
                            <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Email</TableCell>
                            <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Ad Soyad</TableCell>
                            <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>İzin Verilen Tenant'lar</TableCell>
                            <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Oluşturulma</TableCell>
                            <TableCell sx={{ color: "#8b949e", fontWeight: 600 }} align="center">İşlemler</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {vpnUsers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} sx={{ color: "#8b949e", textAlign: "center", py: 4 }}>
                                    Henüz VPN kullanıcısı yok
                                </TableCell>
                            </TableRow>
                        ) : (
                            vpnUsers.map((user) => (
                                <TableRow key={user.id} sx={{ backgroundColor: "#161b22", "&:hover": { backgroundColor: "#21262d" } }}>
                                    <TableCell sx={{ color: "#e6edf3", fontWeight: 600 }}>
                                        {user.username}
                                    </TableCell>
                                    <TableCell sx={{ color: "#e6edf3" }}>{user.email}</TableCell>
                                    <TableCell sx={{ color: "#e6edf3" }}>{user.full_name || "-"}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: "#58a6ff" }}>
                                            {getTenantNames(user.allowed_tenants)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ color: "#e6edf3" }}>
                                        {new Date(user.created_at).toLocaleDateString("tr-TR")}
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            size="small"
                                            sx={{ color: "#a371f7" }}
                                            onClick={() => handleLogsDialogOpen(user)}
                                            title="Access Logları"
                                        >
                                            <VisibilityIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            sx={{ color: "#58a6ff", ml: 0.5 }}
                                            onClick={() => handleEditModalOpen(user)}
                                            title="Düzenle"
                                        >
                                            <EditIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            sx={{ color: "#f85149", ml: 0.5 }}
                                            onClick={() => handleDeleteDialogOpen(user)}
                                            title="Sil"
                                        >
                                            <DeleteIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {vpnUsers.length > 0 && vpnUsers.length < vpnUsersTotal && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => fetchVpnUsers(true)}
                        disabled={loadingMore}
                        sx={{
                            color: "#8b949e",
                            borderColor: "#30363d",
                            "&:hover": { borderColor: "#8b949e" }
                        }}
                    >
                        {loadingMore
                            ? "Yükleniyor..."
                            : `Daha Fazla Yükle (${vpnUsers.length}/${vpnUsersTotal})`
                        }
                    </Button>
                </Box>
            )}

            {/* CREATE MODAL */}
            <Dialog open={showCreateModal} onClose={() => setShowCreateModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    Yeni VPN Kullanıcısı
                    <IconButton size="small" onClick={() => setShowCreateModal(false)} sx={{ color: "#666" }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Username *"
                        fullWidth
                        variant="outlined"
                        value={createForm.username}
                        onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                        sx={{ mb: 2, mt: 1 }}
                    />
                    <TextField
                        margin="dense"
                        label="Email *"
                        type="email"
                        fullWidth
                        variant="outlined"
                        value={createForm.email}
                        onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Ad Soyad"
                        fullWidth
                        variant="outlined"
                        value={createForm.full_name}
                        onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>İzin Verilen Tenant'lar *</InputLabel>
                        <Select
                            multiple
                            value={createForm.allowed_tenants}
                            onChange={(e) => handleTenantChange(e, "create")}
                            input={<OutlinedInput label="İzin Verilen Tenant'lar *" />}
                            renderValue={(selected) => getTenantNames(selected)}
                        >
                            <MenuItem value="*">
                                <Checkbox checked={createForm.allowed_tenants.includes("*")} />
                                <ListItemText primary="Tümü" />
                            </MenuItem>
                            {tenants.map((tenant) => (
                                <MenuItem key={tenant.id} value={tenant.id} disabled={createForm.allowed_tenants.includes("*")}>
                                    <Checkbox checked={createForm.allowed_tenants.includes(tenant.id)} />
                                    <ListItemText primary={tenant.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        margin="dense"
                        label="Şifre *"
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={createForm.password}
                        onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleCreateSubmit}
                        disabled={creating}
                        sx={{ backgroundColor: "#238636", "&:hover": { backgroundColor: "#2ea043" } }}
                    >
                        {creating ? "Oluşturuluyor..." : "Oluştur"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* EDIT MODAL */}
            <Dialog open={showEditModal} onClose={() => setShowEditModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    VPN Kullanıcısı Düzenle
                    <IconButton size="small" onClick={() => setShowEditModal(false)} sx={{ color: "#666" }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <TextField
                        margin="dense"
                        label="Username"
                        fullWidth
                        variant="outlined"
                        value={editingUser?.username || ""}
                        sx={{ mb: 2, mt: 1, backgroundColor: "#f5f5f5" }}
                    />
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Email *"
                        type="email"
                        fullWidth
                        variant="outlined"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Ad Soyad"
                        fullWidth
                        variant="outlined"
                        value={editForm.full_name}
                        onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                        sx={{ mb: 2 }}
                    />
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>İzin Verilen Tenant'lar *</InputLabel>
                        <Select
                            multiple
                            value={editForm.allowed_tenants}
                            onChange={(e) => handleTenantChange(e, "edit")}
                            input={<OutlinedInput label="İzin Verilen Tenant'lar *" />}
                            renderValue={(selected) => getTenantNames(selected)}
                        >
                            <MenuItem value="*">
                                <Checkbox checked={editForm.allowed_tenants.includes("*")} />
                                <ListItemText primary="Tümü" />
                            </MenuItem>
                            {tenants.map((tenant) => (
                                <MenuItem key={tenant.id} value={tenant.id} disabled={editForm.allowed_tenants.includes("*")}>
                                    <Checkbox checked={editForm.allowed_tenants.includes(tenant.id)} />
                                    <ListItemText primary={tenant.name} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Durum</InputLabel>
                        <Select
                            value={editForm.is_active}
                            label="Durum"
                            onChange={(e) => setEditForm({ ...editForm, is_active: e.target.value })}
                        >
                            <MenuItem value={true}>Aktif</MenuItem>
                            <MenuItem value={false}>Pasif</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        margin="dense"
                        label="Yeni Şifre (Boş bırakılabilir)"
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={editForm.password}
                        onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                        placeholder="Değiştirmek istemiyorsanız boş bırakın"
                        sx={{ mb: 2 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleEditSubmit}
                        disabled={updating}
                        sx={{ backgroundColor: "#238636", "&:hover": { backgroundColor: "#2ea043" } }}
                    >
                        {updating ? "Güncelleniyor..." : "Güncelle"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* DELETE DIALOG */}
            <Dialog
                open={showDeleteDialog}
                onClose={() => !deleting && setShowDeleteDialog(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: { backgroundColor: "#161b22", border: "1px solid #f85149", borderRadius: "8px" }
                }}
            >
                <DialogTitle sx={{ color: "#f85149", fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <DeleteIcon />
                        VPN Kullanıcı Silme
                    </Box>
                    <IconButton size="small" onClick={() => setShowDeleteDialog(false)} disabled={deleting} sx={{ color: "#8b949e" }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: "#e6edf3", mb: 2 }}>
                        <strong>"{deletingUser?.username}"</strong> kullanıcısını silmek istediğinize emin misiniz?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleDeleteConfirm}
                        disabled={deleting}
                        sx={{ backgroundColor: "#f85149", "&:hover": { backgroundColor: "#da3633" } }}
                    >
                        {deleting ? "Siliniyor..." : "Evet, Sil"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* VPN SESSIONS DIALOG */}
            <Dialog open={showSessionsDialog} onClose={() => setShowSessionsDialog(false)} maxWidth="xl" fullWidth
                PaperProps={{ sx: { backgroundColor: "#161b22", border: "1px solid #30363d", borderRadius: "8px" } }}
            >
                <DialogTitle sx={{ fontWeight: 600, color: "#e6edf3", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <HistoryIcon sx={{ color: "#58a6ff" }} />
                        VPN Session Logları
                    </Box>
                    <IconButton size="small" onClick={() => setShowSessionsDialog(false)} sx={{ color: "#8b949e" }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {loadingSessions ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : sessions.length === 0 ? (
                        <Typography sx={{ textAlign: "center", py: 6, color: "#8b949e" }}>
                            Henüz session logu bulunmuyor
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Box sx={{ p: 2,  borderBottom: "1px solid #30363d" }}>
                                <TextField
                                    sx={{ backgroundColor: "#fff" }}
                                    size="small"
                                    fullWidth
                                    label="Session ara (kullanıcı, edge, hub)"
                                    value={sessionsSearch}
                                    onChange={(e) => setSessionsSearch(e.target.value)}
                                />
                            </Box>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "#21262d" }}>
                                        <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Kullanıcı</TableCell>
                                        <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Edge</TableCell>
                                        <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Engineer Hub</TableCell>
                                        <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Orijinal Hub</TableCell>
                                        <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Durum</TableCell>
                                        <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Son Heartbeat</TableCell>
                                        <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Bağlanma</TableCell>
                                        <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Bağlantı Kesme</TableCell>
                                        <TableCell sx={{ color: "#8b949e", fontWeight: 600 }}>Süre</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sessions.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} sx={{ color: "#8b949e", textAlign: "center", py: 3 }}>
                                                Session bulunamadı
                                            </TableCell>
                                        </TableRow>
                                    ) : sessions.map((s) => (
                                        <TableRow key={s.id} sx={{ backgroundColor: "#161b22", "&:hover": { backgroundColor: "#21262d" } }}>
                                            <TableCell sx={{ color: "#e6edf3", fontWeight: 600 }}>{s.vpn_username || s.vpn_user_id}</TableCell>
                                            <TableCell sx={{ color: "#e6edf3" }}>{s.edge_asset_id || s.edge_device_id}</TableCell>
                                            <TableCell sx={{ color: "#58a6ff", fontSize: "0.75rem", fontFamily: "monospace" }}>{s.engineer_hub}</TableCell>
                                            <TableCell sx={{ color: "#8b949e", fontSize: "0.75rem", fontFamily: "monospace" }}>{s.original_hub}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={s.is_active ? "Aktif" : "Kapalı"}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: s.is_active ? "#1a4731" : "#2d1b1b",
                                                        color: s.is_active ? "#3fb950" : "#f85149",
                                                        fontWeight: 600,
                                                        fontSize: "0.7rem",
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ color: "#8b949e", fontSize: "0.75rem" }}>
                                                {s.last_heartbeat ? new Date(s.last_heartbeat).toLocaleString("tr-TR") : "-"}
                                            </TableCell>
                                            <TableCell sx={{ color: "#e6edf3", fontSize: "0.75rem" }}>
                                                {s.created_at ? new Date(s.created_at).toLocaleString("tr-TR") : "-"}
                                            </TableCell>
                                            <TableCell sx={{ color: "#8b949e", fontSize: "0.75rem" }}>
                                                {s.disconnected_at ? new Date(s.disconnected_at).toLocaleString("tr-TR") : "-"}
                                            </TableCell>
                                            <TableCell sx={{ color: "#e6edf3", fontSize: "0.75rem", fontWeight: 600 }}>
                                                {formatDuration(s.session_duration_seconds)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    {sessions.length > 0 && sessions.length < sessionsTotal && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={() => fetchSessions(true)}
                                disabled={loadingMoreSessions}
                                sx={{ color: "#8b949e", borderColor: "#30363d", "&:hover": { borderColor: "#8b949e" } }}
                            >
                                {loadingMoreSessions ? "Yükleniyor..." : `Daha Fazla (${sessions.length}/${sessionsTotal})`}
                            </Button>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* ACCESS LOGS DIALOG */}
            <Dialog open={showLogsDialog} onClose={() => setShowLogsDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    Access Logları - {selectedUser?.username}
                    <IconButton size="small" onClick={() => setShowLogsDialog(false)} sx={{ color: "#666" }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {loadingLogs ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : accessLogs.length === 0 ? (
                        <Typography sx={{ textAlign: "center", py: 4, color: "#8b949e" }}>
                            Henüz access log bulunmuyor
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Box sx={{ p: 2, borderBottom: "1px solid #30363d" }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Access log ara (edge, IP)"
                                    value={accessLogsSearch}
                                    onChange={(e) => setAccessLogsSearch(e.target.value)}
                                />
                            </Box>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Edge ID</TableCell>
                                        <TableCell>IP Adresi</TableCell>
                                        <TableCell>Bağlanma Zamanı</TableCell>
                                        <TableCell>Bağlantı Kesme</TableCell>
                                        <TableCell>Süre</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {accessLogs.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} sx={{ textAlign: "center", py: 3, color: "#8b949e" }}>
                                                Access log bulunamadı
                                            </TableCell>
                                        </TableRow>
                                    ) : accessLogs.map((log) => (
                                        <TableRow key={log.id}>
                                            <TableCell>{log.edge_id || "-"}</TableCell>
                                            <TableCell>{log.ip_address || "-"}</TableCell>
                                            <TableCell>
                                                {new Date(log.access_time).toLocaleString("tr-TR")}
                                            </TableCell>
                                            <TableCell>
                                                {log.disconnect_time
                                                    ? new Date(log.disconnect_time).toLocaleString("tr-TR")
                                                    : "Bağlı"}
                                            </TableCell>
                                            <TableCell>{formatDuration(log.session_duration_seconds)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}